// Mock data for Site Detail Admin page

import {
  SiteDetailData,
  SiteProgram,
  EmployeeSegmentsData,
  SiteComplianceData,
  ActivityItem,
} from './siteDetailTypes'

export const mockSitePrograms: SiteProgram[] = [
  {
    id: '1',
    name: 'Commuter Pre-Tax',
    icon: 'dollar',
    budget: 'Variable spend',
    enrolled: 3184,
    eligible: 6024,
    utilization: 81,
    monthlySpend: 753200,
    trend: { value: 8, label: '+8 this month' },
  },
  {
    id: '2',
    name: 'MARTA Transit Pass',
    icon: 'train',
    budget: '$150/employee',
    enrolled: 2112,
    eligible: 6024,
    utilization: 87,
    monthlySpend: 316800,
    trend: { value: 6, label: '+6 this month' },
  },
  {
    id: '3',
    name: 'Micromobility',
    icon: 'bike',
    budget: '$70/mo per employee',
    enrolled: 1512,
    eligible: 6024,
    utilization: 58,
    monthlySpend: 105840,
    trend: { value: 4, label: '+4 this month' },
  },
  {
    id: '4',
    name: 'Employee Parking',
    icon: 'parking',
    budget: '$250/mo per spot',
    enrolled: 712,
    eligible: 960,
    utilization: 95,
    monthlySpend: 178000,
    waitlist: 64,
    trend: { value: 0, label: '' },
  },
  {
    id: '5',
    name: 'Guaranteed Ride Home',
    icon: 'car',
    budget: '6 trips/year',
    enrolled: 2280,
    eligible: 5064,
    utilization: 32,
    monthlySpend: 8176,
    trend: { value: 23, label: '23 trips used this month' },
  },
]

// Flat monthly commuter subsidy, offered only at the two New York airports
// where the NYC commuter benefits law applies.
export const mockSubsidyProgram: SiteProgram = {
  id: 'subsidy-ny',
  name: 'Commuter Subsidy',
  icon: 'dollar',
  budget: '$200/month',
  enrolled: 384,
  eligible: 520,
  utilization: 74,
  monthlySpend: 7680000,
  trend: { value: 5, label: '+5 this month' },
}

export const mockEmployeeSegments: EmployeeSegmentsData = {
  tiers: [
    {
      id: '1',
      name: '5 days/week',
      color: '#57112C', // pink800
      employeeCount: 488,
      stipendAmount: 200,
    },
    {
      id: '2',
      name: '4 days/week',
      color: '#94254F', // pink700
      employeeCount: 1144,
      stipendAmount: 150,
    },
    {
      id: '3',
      name: '3 days/week',
      color: '#D23873', // pink600
      employeeCount: 1136,
      stipendAmount: 100,
    },
    {
      id: '4',
      name: 'Remote',
      color: '#F06F98', // pink500
      employeeCount: 488,
      stipendAmount: 50,
    },
  ],
  rules: {
    description:
      'Stipend amount is based on RTO commitment level. Employees who commit to more in-office days receive higher monthly stipends to offset commute costs.',
    totalMonthlySpend: 407000,
    avgPerEmployee: 125,
  },
}

export const mockSiteCompliance: SiteComplianceData = {
  status: 'Compliant',
  statusDescription: 'All requirements met for this jurisdiction',
  items: [
    { id: '1', label: 'Commuter Benefits Ordinance', completed: true },
    { id: '2', label: 'TDM Plan Filed', completed: true },
    { id: '3', label: 'Annual Survey Submitted', completed: true },
    {
      id: '4',
      label: 'Next Report Due',
      completed: false,
      dueDate: 'Mar 15, 2026',
      status: 'warning',
    },
  ],
  requirements: [
    { text: 'Employers with 20+ employees must offer commuter benefits' },
    {
      text: 'Pre-tax transit benefits or employer-paid transit subsidy required',
    },
    {
      text: 'Annual commuter survey required for employers with 50+ employees',
    },
    { text: 'TDM plan required for sites with 100+ employees' },
  ],
}

export const mockSiteActivities: ActivityItem[] = [
  {
    id: '1',
    employeeName: 'Sarah Chen',
    initials: 'SC',
    action: 'enrolled in',
    highlightedText: 'baypass',
    timestamp: '2 hours ago',
  },
  {
    id: '2',
    employeeName: 'Mike Johnson',
    initials: 'MJ',
    action: 'updated pre-tax election',
    timestamp: '3 hours ago',
  },
  {
    id: '3',
    employeeName: 'Emily Davis',
    initials: 'ED',
    action: 'requested guaranteed ride home',
    timestamp: '5 hours ago',
  },
  {
    id: '4',
    employeeName: 'Alex Kim',
    initials: 'AK',
    action: 'enrolled in',
    highlightedText: 'ridepanda',
    timestamp: '1 day ago',
  },
  {
    id: '5',
    employeeName: 'Jordan Lee',
    initials: 'JL',
    action: 'linked clipper card',
    timestamp: '1 day ago',
  },
]

export const mockSiteDetail: SiteDetailData = {
  id: '1',
  name: 'Atlanta',
  address: '1030 Delta Blvd, Atlanta, GA 30354',
  employeeCount: 3256,
  enrolledCount: 2800,
  participationRate: 86,
  lastSync: '2 hours ago',
  complianceStatus: 'Compliant',
  programs: mockSitePrograms,
  segments: mockEmployeeSegments,
  compliance: mockSiteCompliance,
  activities: mockSiteActivities,
}

// Mock data for other sites
export const mockSitesByName: Record<string, SiteDetailData> = {
  atlanta: mockSiteDetail,
  minneapolis: {
    ...mockSiteDetail,
    id: '2',
    name: 'Minneapolis',
    address: '4300 Glumack Dr, St Paul, MN 55111',
    employeeCount: 1312,
    enrolledCount: 1064,
    participationRate: 81,
    lastSync: '3 hours ago',
  },
  'salt-lake-city': {
    ...mockSiteDetail,
    id: '3',
    name: 'Salt Lake City',
    address: '776 N Terminal Dr, Salt Lake City, UT 84122',
    employeeCount: 792,
    enrolledCount: 616,
    participationRate: 78,
    lastSync: '1 hour ago',
  },
  detroit: {
    ...mockSiteDetail,
    id: '4',
    name: 'Detroit',
    address: '1 Renaissance Center, Detroit, MI 48243',
    employeeCount: 536,
    enrolledCount: 344,
    participationRate: 64,
    lastSync: '5 hours ago',
    complianceStatus: 'Action Needed',
    compliance: {
      ...mockSiteCompliance,
      status: 'Action Needed',
      statusDescription:
        'Outstanding commuter benefit requirement for this jurisdiction',
    },
  },
  'new-york-jfk': {
    ...mockSiteDetail,
    programs: [...mockSitePrograms, mockSubsidyProgram],
    id: '5',
    name: 'New York JFK',
    address: 'Building 144, JFK International Airport, Jamaica, NY 11430',
    employeeCount: 520,
    enrolledCount: 456,
    participationRate: 88,
    lastSync: '2 hours ago',
  },
  'los-angeles': {
    ...mockSiteDetail,
    id: '6',
    name: 'Los Angeles',
    address: '6040 Avion Dr, Los Angeles, CA 90045',
    employeeCount: 520,
    enrolledCount: 432,
    participationRate: 83,
    lastSync: '4 hours ago',
  },
  seattle: {
    ...mockSiteDetail,
    id: '7',
    name: 'Seattle',
    address: '17801 International Blvd, Seattle, WA 98158',
    employeeCount: 408,
    enrolledCount: 368,
    participationRate: 90,
    lastSync: '1 hour ago',
  },
  boston: {
    ...mockSiteDetail,
    id: '8',
    name: 'Boston',
    address: '1 Harborside Dr, Boston, MA 02128',
    employeeCount: 328,
    enrolledCount: 256,
    participationRate: 79,
    lastSync: '6 hours ago',
  },
  'new-york-lga': {
    ...mockSiteDetail,
    programs: [...mockSitePrograms, mockSubsidyProgram],
    id: '9',
    name: 'New York LGA',
    address: 'Grand Central Pkwy, Flushing, NY 11371',
    employeeCount: 328,
    enrolledCount: 240,
    participationRate: 72,
    lastSync: '3 hours ago',
    complianceStatus: 'Action Needed',
    compliance: {
      ...mockSiteCompliance,
      status: 'Action Needed',
      statusDescription:
        'Outstanding commuter benefit requirement for this jurisdiction',
    },
  },
}
